import {useCallback, useState} from 'react';

import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';
import {local} from 'lib/StorageUtil';

import type {HomeTourStepId} from './homeTourConfig';
import {
  getHomeTourStorageKey,
  HOME_TOUR_STEPS,
  isHomeTourRoute,
  isPreLaunchAccount
} from './homeTourConfig';

export interface HomeTour {
  /**
   * Whether this teacher is in the announcement cohort on an announcement route. Constant for the
   * lifetime of a mount, so callouts can wrap their anchor without remounting it on dismissal.
   */
  isEligible: boolean;
  activeStepId: HomeTourStepId | null;
  stepNumber: number;
  totalSteps: number;
  isLastStep: boolean;
  /** Advances to the next step, or dismisses (and persists) after the last one. */
  advance: () => void;
  /** Dismisses (and persists) from any step. */
  dismiss: () => void;
}

/**
 * Drives the one-time Home tour: cohort gate, step cursor, and the per-user dismissal flag. The
 * storage key uses the real logged-in user (not the masqueraded one) because it records what the
 * person at the keyboard has already seen — mirroring `TeacherDashboard`'s `homeHideGetStarted`.
 */
export const useHomeTour = (pathname: string): HomeTour => {
  const userId = useFluxStore(sessionStore, (s) => s.getUserId());
  const signUpDate = useFluxStore(sessionStore, (s) => s.getSignUpDate());

  const storageKey = getHomeTourStorageKey(userId ?? 'not-logged-in');

  // Read once on mount: a login swap on a shared device reloads the app, which re-reads this.
  const [wasDismissedBeforeMount] = useState(() => Boolean(local.get(storageKey)));
  const [isDismissed, setIsDismissed] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const isEligible =
    isHomeTourRoute(pathname) && !wasDismissedBeforeMount && isPreLaunchAccount(signUpDate);

  const dismiss = useCallback(() => {
    local.set(storageKey, true);
    setIsDismissed(true);
  }, [storageKey]);

  const isLastStep = stepIndex >= HOME_TOUR_STEPS.length - 1;

  const advance = useCallback(() => {
    if (stepIndex >= HOME_TOUR_STEPS.length - 1) {
      dismiss();
      return;
    }
    setStepIndex(stepIndex + 1);
  }, [dismiss, stepIndex]);

  return {
    isEligible,
    activeStepId: isEligible && !isDismissed ? HOME_TOUR_STEPS[stepIndex].id : null,
    stepNumber: stepIndex + 1,
    totalSteps: HOME_TOUR_STEPS.length,
    isLastStep,
    advance,
    dismiss
  };
};
